/**
 * Vanturis Passport Phase 8 — centralized Passport API client boundary.
 *
 * Every Passport screen talks to the backend through this module only,
 * using the shared cookie-credentialed Vanturis axios instance.  A future
 * standalone Passport client (web or mobile) replaces this one module —
 * no Passport screen depends on Marketplace state or components for data.
 */
import api from "@/lib/api";

const unwrap = (p) => p.then((r) => r.data);

export const passportApi = {
  // Phase 8 — navigation capability summary (NOT an authorization token)
  capabilities: () => unwrap(api.get("/passport/me/capabilities")),

  // Phase 7 — partitioned asset list (server-authoritative classification)
  assetsPartitioned: () => unwrap(api.get("/passport/assets")),

  // Phase 4 — owner experience
  assetSummary: (assetId) => unwrap(api.get(`/passport/assets/${assetId}`)),
  assetTimeline: (assetId, limit = 25) =>
    unwrap(api.get(`/passport/assets/${assetId}/timeline`, { params: { limit } })),
  assetDocuments: (assetId) => unwrap(api.get(`/passport/assets/${assetId}/documents`)),
  assetInspections: (assetId) => unwrap(api.get(`/passport/assets/${assetId}/inspections`)),
  // Full Inspection Report — structured customer-safe detail for a bridged
  // Vanturis inspection (authorized owner/manager/shared-viewer).
  inspectionDetail: (assetId, eventId) =>
    unwrap(api.get(`/passport/assets/${assetId}/inspections/${eventId}/detail`)),
  // Fetch the official PDF as a blob so a failed request never dumps raw
  // JSON into a browser tab — the caller shows a proper error state instead.
  inspectionReportPdf: (assetId, eventId) =>
    api.get(`/passport/assets/${assetId}/inspections/${eventId}/report.pdf`, { responseType: "blob" })
      .then((r) => r.data),
  documentDownload: (documentId, assetId) =>
    unwrap(api.get(`/passport/documents/${documentId}/download`, { params: { asset_id: assetId } })),
  inspectionReport: (inspectionId, assetId) =>
    unwrap(api.get(`/passport/inspections/${inspectionId}/report`, { params: { asset_id: assetId } })),
  // Inspector → Passport bridge — authorized PDF proxy URL (cookie-credentialed
  // top-level navigation; the backend authorizes owner/manager/shared-with-ACL).
  bridgedReportUrl: (assetId, eventId) =>
    `${api.defaults.baseURL}/passport/assets/${assetId}/inspections/${eventId}/report.pdf`,

  // Phase 7 — owner share management
  listShares: (assetId) => unwrap(api.get(`/passport/assets/${assetId}/shares`)),
  createShare: (assetId, body) => unwrap(api.post(`/passport/assets/${assetId}/shares`, body)),
  revokeShare: (grantId, reason) =>
    unwrap(api.post(`/passport/shares/${grantId}/revoke`, { reason: reason || null })),
  revokeInvitation: (invitationId, reason) =>
    unwrap(api.post(`/passport/shares/invitations/${invitationId}/revoke`, { reason: reason || null })),

  // Phase 7 — authenticated invitation redemption
  redeemShare: (token) => unwrap(api.post("/passport/shares/redeem", { token })),

  // Phase 7 — shared-viewer surface (safe summary / timeline / shared docs)
  sharedSummary: (assetId) => unwrap(api.get(`/passport/shared/assets/${assetId}/summary`)),
  sharedTimeline: (assetId) => unwrap(api.get(`/passport/shared/assets/${assetId}/timeline`)),
  sharedDocuments: (assetId) => unwrap(api.get(`/passport/shared/assets/${assetId}/documents`)),
  sharedDocumentSignedUrl: (assetId, documentId) =>
    unwrap(api.get(`/passport/shared/assets/${assetId}/documents/${documentId}/signed-url`)),

  // Phase 3 — ownership claims (claimant surface). The endpoint returns a
  // bare array; normalize legacy {claims:[...]} shapes to an array here.
  myClaims: () => unwrap(api.get("/passport/claims/mine"))
    .then((r) => (Array.isArray(r) ? r : (r?.claims || []))),

  // P1 — customer claim ENTRY from a Marketplace listing
  listingClaimContext: (listingId) => unwrap(api.get(`/passport/listings/${listingId}/claim-context`)),
  startListingClaim: (listingId, body) => unwrap(api.post(`/passport/listings/${listingId}/claim`, body || {})),
};

/** Normalize backend availability errors: while PASSPORT_ENABLED=false every
 * Passport route returns 404, which the UI renders as "coming soon". */
export function passportErrorKind(err) {
  const s = err?.response?.status;
  if (s === 404) return "unavailable";
  if (s === 403) return "forbidden";
  if (s === 401) return "unauthenticated";
  return "error";
}
